import React, { ChangeEvent, FormEvent, useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, Container, Stack } from '@mui/material';
import TextField from '@mui/material/TextField';

import PlaceLookUp from '~/components/Common/Forms/PlaceLookUp';
import { ManualExperienceLocation } from '~/components/Content/ManualExperienceLocations/Columns';
import {
  TYPEAHEAD_EXPERIENCE_OFFER_TYPES,
  TYPEAHEAD_PLACE_TYPES,
} from '~/components/Content/ManualExperienceLocations/constants';

import useCurrentUser from '~/hooks/useCurrentUser';

import SearchService, { TypeaheadSearchResult } from '~/services/SearchService';

type Props = {
  onClose: () => void;
  data?: Array<ManualExperienceLocation>;
};

function AddManualExperienceLocationForm({ onClose, data = [] }: Props) {
  const [experienceOffer, setExperienceOffer] = useState<TypeaheadSearchResult | undefined>(undefined);
  const [place, setPlace] = useState<TypeaheadSearchResult | undefined>(undefined);
  const [comments, setComments] = useState<string>('');

  const { user } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  const handleSelectedExperienceOffer = useCallback((selection: TypeaheadSearchResult | undefined) => {
    setExperienceOffer(selection);
  }, []);

  const handleSelectedPlace = useCallback((selection: TypeaheadSearchResult | undefined) => {
    setPlace(selection);
  }, []);

  const handleCommentsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setComments(event.target.value);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!experienceOffer || !place) {
      enqueueSnackbar('Please select both an experience offer and a place', { variant: 'error' });
      return;
    }

    if (data.some((item) => item.experienceOfferId === experienceOffer.id && item.placeId === place.id)) {
      enqueueSnackbar('Experience location association already exists', { variant: 'error' });
      return;
    }

    try {
      const { status } = await SearchService.putExperienceManualLocation(
        experienceOffer.id,
        place.id,
        user.fullName,
        comments,
      );
      if (status === 200) {
        enqueueSnackbar('Added experience location association', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to add experience location association', { variant: 'error' });
      }

      onClose();
    } catch (e) {
      enqueueSnackbar(`Failed to add experience location association: ${e}`, { variant: 'error' });
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      <form onSubmit={handleSubmit}>
        <Stack direction="column" gap={4}>
          <PlaceLookUp
            onCurrentValueChange={handleSelectedExperienceOffer}
            types={TYPEAHEAD_EXPERIENCE_OFFER_TYPES}
            label="Experience Offer"
            vertical="experience"
          />
          <PlaceLookUp onCurrentValueChange={handleSelectedPlace} types={TYPEAHEAD_PLACE_TYPES} label="Place" />

          <TextField
            label="Comments (Optional)"
            variant="outlined"
            multiline
            rows={2}
            fullWidth
            placeholder="Enter any comments here"
            value={comments}
            onChange={handleCommentsChange}
          />

          <Stack direction="column" gap={2}>
            <Button variant="contained" color="primary" type="submit" disabled={!experienceOffer || !place}>
              Submit
            </Button>
            <Button variant="contained" color="secondary" onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </form>
    </Container>
  );
}

export default AddManualExperienceLocationForm;
